/**
 * OrderReadOnlyView Component
 *
 * Read-only view for non-draft orders (sent, partially received, received, cancelled).
 * Shows status banner, progress, items, notes, and linked receipts.
 * Designed to be rendered inside a Drawer.
 */

import { Badge, Modal, ModalFooter, Button } from '@/components/ui';
import { OrderStatusBanner } from './OrderStatusBanner';
import { OrderItemsTableEnriched } from '../OrderItemsTableEnriched';
import { OrderReceiptsProgress } from '../OrderReceiptsProgress';
import { OrderLinkedReceipts } from '../OrderLinkedReceipts';
import { DocumentSection } from '@/features/documents';
import { PurchaseOrderStatus, PURCHASE_ORDER_STATUS_LABELS } from '@bstock/shared';
import type { PurchaseOrderDetailResponse } from '@bstock/shared';
import { XCircle } from 'lucide-react';

const ORDER_STATUS_VARIANTS: Record<PurchaseOrderStatus, 'warning' | 'success' | 'neutral' | 'primary'> = {
  [PurchaseOrderStatus.DRAFT]: 'warning',
  [PurchaseOrderStatus.SENT]: 'success',
  [PurchaseOrderStatus.PARTIALLY_RECEIVED]: 'primary',
  [PurchaseOrderStatus.RECEIVED]: 'success',
  [PurchaseOrderStatus.CANCELLED]: 'neutral',
};

interface OrderReadOnlyViewProps {
  order: PurchaseOrderDetailResponse;
  onCancelOrder: () => void;
  showCancelConfirm: boolean;
  onCloseCancelConfirm: () => void;
  isCancelling: boolean;
  showFinancials?: boolean;
}

export function OrderReadOnlyView({
  order,
  onCancelOrder,
  showCancelConfirm,
  onCloseCancelConfirm,
  isCancelling,
  showFinancials = false,
}: OrderReadOnlyViewProps) {
  const isSent = order.status === PurchaseOrderStatus.SENT;
  const isPartiallyReceived = order.status === PurchaseOrderStatus.PARTIALLY_RECEIVED;
  const isReceived = order.status === PurchaseOrderStatus.RECEIVED;

  // Calculate reception progress
  let totalOrdered = 0;
  let totalReceived = 0;
  let completedLines = 0;
  for (const item of order.items) {
    totalOrdered += item.quantity;
    totalReceived += item.quantityReceived;
    if (item.quantityReceived >= item.quantity) {
      completedLines++;
    }
  }

  return (
    <>
      {/* Status badge */}
      <div className="mb-4">
        <Badge variant={ORDER_STATUS_VARIANTS[order.status]}>
          {PURCHASE_ORDER_STATUS_LABELS[order.status]}
        </Badge>
      </div>

      {/* Status banner */}
      <OrderStatusBanner status={order.status} />

      {/* Reception Progress (for sent/partially received/received orders) */}
      {(isSent || isPartiallyReceived || isReceived) && (
        <OrderReceiptsProgress
          totalOrdered={totalOrdered}
          totalReceived={totalReceived}
          completedLines={completedLines}
          totalLines={order.items.length}
          className="mb-6"
        />
      )}

      {/* Enriched items table with Recu/Reste columns */}
      <OrderItemsTableEnriched items={order.items} showFinancials={showFinancials} />

      {/* Notes */}
      {order.notes && (
        <div className="mt-6 pt-6 border-t border-neutral-200">
          <h3 className="text-sm font-medium text-neutral-700 mb-2">Notes</h3>
          <p className="text-sm text-neutral-600">{order.notes}</p>
        </div>
      )}

      {/* Linked Receipts + Documents — side by side */}
      <div className="mt-6 pt-6 border-t border-neutral-200 flex flex-col lg:flex-row gap-6">
        {(isSent || isPartiallyReceived || isReceived) && (
          <div className="flex-1 min-w-0">
            <OrderLinkedReceipts
              orderId={order.id}
              showCreateButton={false}
              showFinancials={showFinancials}
            />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <DocumentSection entityRoute="purchase-orders" entityId={order.id} inline />
        </div>
      </div>

      {/* Cancel confirmation modal */}
      <Modal
        isOpen={showCancelConfirm}
        onClose={onCloseCancelConfirm}
        title="Annuler la commande ?"
        size="sm"
      >
        <p className="text-sm text-neutral-600 mb-4">
          Etes-vous sur de vouloir annuler la commande <strong>{order.orderNumber}</strong> ?
          Cette action est irreversible.
        </p>
        <ModalFooter>
          <Button variant="ghost" onClick={onCloseCancelConfirm}>
            Non, conserver
          </Button>
          <Button variant="danger" onClick={onCancelOrder} disabled={isCancelling}>
            <XCircle className="w-4 h-4" />
            Oui, annuler
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
